import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';

/**
 * Upload a file to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} groupId - The group/band ID
 * @param {string} docId - Unique document ID
 * @param {Function} onProgress - Optional progress callback (0-100)
 * @returns {Promise<{storagePath: string, downloadUrl: string, size: number}>}
 */
export const uploadDocumentToStorage = async (file, groupId, docId, onProgress) => {
  // Create a unique path: groups/{groupId}/documents/{docId}/{filename}
  const storagePath = `groups/${groupId}/documents/${docId}/${file.name}`;
  const storageRef = ref(storage, storagePath);

  try {
    // Upload the file
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString()
      }
    });

    // Get the download URL
    const downloadUrl = await getDownloadURL(snapshot.ref);

    return {
      storagePath,
      downloadUrl,
      size: file.size
    };
  } catch (error) {
    console.error('Error uploading to Storage:', error);
    throw error;
  }
};

/**
 * Delete a file from Firebase Storage
 * @param {string} storagePath - The path to the file in Storage
 */
export const deleteDocumentFromStorage = async (storagePath) => {
  if (!storagePath) return;
  
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    // Ignore "not found" errors (file may already be deleted)
    if (error.code !== 'storage/object-not-found') {
      console.error('Error deleting from Storage:', error);
      throw error;
    }
  }
};

/**
 * Compress an image file before upload
 * @param {File} file - The image file to compress
 * @param {number} maxSizeKB - Target max size in KB
 * @param {number} maxDimension - Max width/height in pixels
 * @returns {Promise<File>}
 */
export const compressImage = (file, maxSizeKB = 800, maxDimension = 1920) => {
  return new Promise((resolve) => {
    // Skip non-images or already small files
    if (!file.type.startsWith('image/') || file.size <= maxSizeKB * 1024) {
      resolve(file);
      return;
    }

    // Skip HEIC files (browser can't process them)
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.heic') || fileName.endsWith('.heif') || 
        file.type === 'image/heic' || file.type === 'image/heif') {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Scale down if too large
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File(
                [blob], 
                file.name.replace(/\.[^.]+$/, '.jpg'), 
                { type: 'image/jpeg', lastModified: Date.now() }
              );
              console.log(`Compressed ${file.name}: ${(file.size/1024).toFixed(0)}KB → ${(blob.size/1024).toFixed(0)}KB`);
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          0.8
        );
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

/**
 * Process and upload a document file
 * This is the main function to use from components
 * 
 * @param {File} file - The file to upload
 * @param {string} groupId - The group/band ID
 * @param {Object} metadata - Additional metadata (category, tags, notes, etc.)
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Object>} Document object ready to save to Firestore
 */
export const processAndUploadDocument = async (file, groupId, metadata = {}, onProgress) => {
  const docId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // Compress images before upload
  const processedFile = await compressImage(file);
  
  // Upload to Storage
  const { storagePath, downloadUrl, size } = await uploadDocumentToStorage(
    processedFile, 
    groupId, 
    docId,
    onProgress
  );

  // Return document object (NO base64 data - just the URL!)
  return {
    id: docId,
    name: metadata.customName || file.name,
    originalName: file.name,
    size: size,
    type: processedFile.type,
    storagePath: storagePath,      // Path in Firebase Storage
    downloadUrl: downloadUrl,       // URL to access the file
    uploadedAt: new Date().toISOString(),
    category: metadata.category || 'other',
    showId: metadata.showId || null,
    tags: metadata.tags || [],
    notes: metadata.notes || '',
    favorite: false
  };
};